/* =========================================================================
 * The Command Center's share of the 2026-10-26 build, in ONE hook so the
 * page itself changes by a handful of lines (four lanes edit that page today).
 * [Jarvis · Claude]
 *
 *   dateFilter / setDateFilter   the shared date control, held in the URL
 *   dateSurface                  which date this view searches (dateFilter.js)
 *   moveCard(c, to)              a Pursuit board move: write, confirm, patch
 * ========================================================================= */
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { readDateFilter, writeDateFilter, ccSurfaceFor, SURFACE_DATES } from '../../../lib/dateFilter.js'
import { createPursuitMoveController } from '../../../lib/pursuitMoveState.js'
import { useSessionGeneration } from '../../../lib/useSessionGeneration.js'
import { movePursuit } from '../../../lib/pursuitMoveApi.js'

export function useBoardControls({ params, setParams, view, category, dated, demo, patchLocal, setDrawer }) {
  const dateFilter = useMemo(() => readDateFilter(params), [params])
  const setDateFilter = useCallback(
    (next) => setParams((previous) => writeDateFilter(previous, next), { replace: true }),
    [setParams],
  )
  const dateSurface = ccSurfaceFor(view, category, dated)
  const dateSpec = SURFACE_DATES[dateSurface]

  /* A MOVE LANDS ONLY WHEN THE SERVER SAYS IT WROTE THE FIELD. The card is
   * patched after the echo, never before — so a refusal can never leave a
   * card sitting in a column the record is not in. Championship plane only:
   * a pursuit-space record lives in another object and moves through its own
   * route from the record, so no `onMove` is offered there. */
  const canMove = category?.source === 'championships'
  const generation = useSessionGeneration()
  const callbacks = useRef({patchLocal, setDrawer})
  callbacks.current = {patchLocal, setDrawer}
  const control = useRef(null)
  useEffect(() => {
    const next = createPursuitMoveController({
      send: movePursuit,
      onLanded: (id, to) => {
        callbacks.current.patchLocal(id, {pursuit: to})
        callbacks.current.setDrawer?.(d => d && String(d.id) === String(id) ? {...d, pursuit: to} : d)
      },
    })
    control.current = next
    return () => { next.dispose(); if (control.current === next) control.current = null }
  }, [generation, view, category?.key, canMove])
  const moveCard = useCallback(async (c, to) => {
    if (demo) { patchLocal(c.id, {pursuit: to}); return {landed: true, to, demo: true} }
    return control.current?.move(c, to) || {landed: false, reason: 'The board is changing. Wait for its current read.'}
  }, [demo, patchLocal])

  return { dateFilter, setDateFilter, dateSurface, dateSpec, moveCard: canMove ? moveCard : null }
}
